import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowDown, Play, ArrowRight, Inbox, Cpu, Sparkles, Truck, Scale, Megaphone, X } from "lucide-react";
import Reveal from "../components/Reveal";
import ProgressArcCounter from "../components/ProgressArcCounter";
import DocumentScanner from "../components/DocumentScanner";
import SectionDivider from "../components/SectionDivider";
import SpotlightCard from "../components/SpotlightCard";
import IntegrationTicker from "../components/IntegrationTicker";
import SEO from "../components/SEO";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Zelaflo Labs",
  "url": "https://zelaflo.com",
  "email": "hello@zelaflo.com",
  "description": "Business automation and document triage services",
  "serviceType": ["Business automation", "Document automation", "Workflow automation", "Invoice processing automation"],
  "areaServed": "Worldwide",
};

const stats = [
  { value: 48, suffix: "h", label: "Average Delivery Time", percent: 0.7 },
  { value: 10, suffix: "+", label: "Hours Saved Per Week Per Client", percent: 0.55 },
  { value: 100, suffix: "%", label: "Done For You", percent: 1.0 },
  { value: 0, suffix: "", label: "Manual Work Required", percent: 0.05 },
];

const steps = [
  {
    n: "01",
    title: "Documents Arrive",
    body: "Your team sends documents the same way they always have — by email. Nothing changes on your end.",
    icon: Inbox,
  },
  {
    n: "02",
    title: "Instant Processing",
    body: "Our system reads every document, extracts the key information, and logs it automatically into your dashboard.",
    icon: Cpu,
  },
  {
    n: "03",
    title: "You Stay Informed",
    body: "A clean summary lands in your inbox instantly. Deadlines, amounts, sender details — everything you need, organized and ready.",
    icon: Sparkles,
  },
];

const industries = [
  {
    icon: Truck,
    name: "Logistics & Freight",
    body: "Rate confirmations, bills of lading, and load sheets processed automatically.",
  },
  {
    icon: Scale,
    name: "Legal & Professional Services",
    body: "Contracts, retainer agreements, and client briefs handled without lifting a finger.",
  },
  {
    icon: Megaphone,
    name: "Marketing & Creative Agencies",
    body: "Proposals, invoices, and project briefs organized the moment they arrive.",
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div data-testid="home-page">
      <SEO
        title="Zelaflo Labs — Business Automation Services | Document Triage & Workflow Automation"
        description="Zelaflo Labs builds custom business automation systems that eliminate manual document work. Invoices, contracts, and proposals processed automatically in under 30 seconds. Small business automation that just works."
        path="/"
        jsonLd={homeJsonLd}
      />
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-center pt-28 pb-20" data-testid="hero-section">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-medium backdrop-blur-md" style={{ backgroundColor: "var(--card-tint)", borderColor: "var(--border-strong)", color: "var(--text-muted)" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#7B2FBE] animate-pulse" />
              Business Automation Agency
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-semibold tracking-tighter max-w-5xl text-balance leading-[1.02]" style={{ color: "var(--text)" }}>
              Your Business on{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-[#8A4FFF] via-[#7B2FBE] to-[#2DD4BF] bg-clip-text text-transparent">Autopilot.</span>
                <motion.span
                  className="absolute left-0 right-0 bottom-1 h-[6px] -z-10 rounded-sm"
                  style={{ background: "linear-gradient(90deg, rgba(123,47,190,0.3), rgba(45,212,191,0.3))" }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1, delay: 0.7 }}
                />
              </span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg md:text-xl max-w-2xl leading-relaxed" style={{ color: "var(--text-muted)" }}>
              We build custom automation systems that eliminate manual work, reduce errors, and save your team hours every week — without changing the tools you already use.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setDemoOpen(true)}
                className="btn-primary-zl inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
                data-testid="hero-watch-demo-btn"
              >
                <Play size={16} fill="currentColor" /> Watch the Demo
              </button>
              <a
                href="/contact"
                onClick={(e) => { e.preventDefault(); navigate("/contact"); }}
                className="btn-outline-zl inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
                data-testid="hero-free-sample-btn"
              >
                Get a Free Sample <ArrowRight size={16} />
              </a>
            </div>
          </Reveal>
          </div>

          <div className="lg:col-span-5 hidden md:block">
            <Reveal delay={0.4}>
              <DocumentScanner />
            </Reveal>
          </div>
        </div>

        <motion.button
          onClick={() => window.scrollTo({ top: window.innerHeight - 70, behavior: "smooth" })}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hover:opacity-100 transition-opacity"
          style={{ color: "var(--text-muted)" }}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2.5, repeat: Infinity }}
          data-testid="hero-scroll-arrow"
          aria-label="Scroll down"
        >
          <ArrowDown size={22} />
        </motion.button>
      </section>

      <SectionDivider />

      {/* STATS */}
      <section className="relative py-24 md:py-32" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">By the numbers</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold max-w-3xl tracking-tighter" style={{ color: "var(--text)" }}>Quietly removing the boring work.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <ProgressArcCounter
                  end={s.value}
                  suffix={s.suffix}
                  label={s.label}
                  percent={s.percent}
                  testId={`stat-${i}`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* INTEGRATIONS TICKER */}
      <section className="relative py-20 md:py-24" data-testid="integrations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 mb-10 md:mb-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">Integrations</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-3xl mx-auto" style={{ color: "var(--text)" }}>
              Works With Your Stack
            </h2>
            <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "var(--text-muted)" }}>
              We connect the tools you already use into one seamless system.
            </p>
          </Reveal>
        </div>
        <Reveal>
          <IntegrationTicker />
        </Reveal>
      </section>

      <SectionDivider />

      {/* HOW IT WORKS */}
      <section className="relative py-24 md:py-32" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">How It Works</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold max-w-3xl tracking-tighter" style={{ color: "var(--text)" }}>From inbox to insight, automatically.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <SpotlightCard
                  className="group h-full p-8 md:p-10 rounded-2xl border transition-all duration-500 hover:-translate-y-2"
                  style={{ backgroundColor: "var(--surface)", borderColor: "var(--border)" }}
                  glowColor="rgba(123,47,190,0.18)"
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-12 h-12 rounded-xl bg-[#7B2FBE]/15 border border-[#7B2FBE]/30 flex items-center justify-center text-[#8A4FFF]">
                      <s.icon size={22} />
                    </div>
                    <span className="font-heading text-5xl font-bold tracking-tighter group-hover:text-[#7B2FBE]/50 transition-colors duration-500" style={{ color: "var(--text-subtle)", opacity: 0.18 }}>{s.n}</span>
                  </div>
                  <h3 className="font-heading text-2xl font-semibold mb-3" style={{ color: "var(--text)" }}>{s.title}</h3>
                  <p className="leading-relaxed text-base" style={{ color: "var(--text-muted)" }}>{s.body}</p>
                </SpotlightCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* SOCIAL PROOF / INDUSTRIES */}
      <section className="relative py-24 md:py-32" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold max-w-4xl tracking-tighter text-balance" style={{ color: "var(--text)" }}>
              Built for businesses that are <span className="text-[#7B2FBE]">serious</span> about efficiency.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {industries.map((ind, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <SpotlightCard
                  className="group p-8 rounded-2xl border h-full transition-all duration-500 hover:-translate-y-1"
                  style={{ backgroundColor: "var(--surface)", borderColor: "var(--border)" }}
                  glowColor="rgba(45,212,191,0.16)"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#7B2FBE] flex items-center justify-center text-white mb-6 group-hover:rotate-6 transition-transform duration-500 zl-on-purple">
                    <ind.icon size={24} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-3" style={{ color: "var(--text)" }}>{ind.name}</h3>
                  <p className="leading-relaxed" style={{ color: "var(--text-muted)" }}>{ind.body}</p>
                </SpotlightCard>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-20 p-10 md:p-16 rounded-3xl bg-[#7B2FBE] relative overflow-hidden">
              <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-[#2DD4BF]/30 blur-3xl pointer-events-none" />
              <div className="absolute -left-20 -bottom-20 w-80 h-80 rounded-full bg-white/10 blur-3xl pointer-events-none" />
              <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8 zl-on-purple">
                <div className="max-w-2xl">
                  <h3 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white">Ready to take the boring work off your team?</h3>
                  <p className="mt-4 text-white/85">Let's design an automation that fits your workflow — live in 48 hours.</p>
                </div>
                <button
                  onClick={() => navigate("/contact")}
                  className="inline-flex items-center gap-2 bg-white text-[#171412] hover:bg-white/90 hover:scale-[1.03] transition-all duration-300 px-7 py-4 rounded-full text-sm font-semibold whitespace-nowrap zl-on-light"
                  data-testid="home-cta-talk-btn"
                >
                  Let's Talk <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Demo Modal */}
      {demoOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
          onClick={() => setDemoOpen(false)}
          data-testid="demo-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="w-full max-w-4xl bg-[#1E1A18] border border-white/10 rounded-2xl p-4 md:p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDemoOpen(false)}
              className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white text-[#171412] hover:scale-110 flex items-center justify-center transition-transform shadow-lg zl-on-light"
              data-testid="demo-modal-close"
              aria-label="Close demo"
            >
              <X size={18} />
            </button>
            <div className="aspect-video rounded-xl overflow-hidden bg-black border border-white/10">
              <video
                src="https://customer-assets.emergentagent.com/job_workflow-builder-125/artifacts/oajkh044_Zelaflo%20Demo.mp4"
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="w-full h-full"
                data-testid="demo-video"
              >
                Sorry, your browser does not support embedded videos.
              </video>
            </div>
            <p className="text-white/55 text-sm mt-4 text-center">
              Want a personalized walkthrough? <a href="mailto:hello@zelaflo.com" className="text-[#2DD4BF] hover:underline">Email us</a>.
            </p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Home;
